import { useEffect } from 'react';
import { incrementView } from './viewer-api';

/**
 * Incrément du COMPTEUR DE VUES d'une photo réellement REGARDÉE (LOT 1, section C).
 *
 * Deux garde-fous pour ne compter qu'une vue « intentionnelle » :
 *  - DEBOUNCE (`VIEW_DEBOUNCE_MS`) : on n'incrémente qu'après que l'asset est resté
 *    l'asset courant assez longtemps → un va-et-vient préc./suiv. RAPIDE ne gonfle
 *    pas le compteur des images survolées (le minuteur est annulé au changement).
 *  - DÉDOUBLONNAGE DE SESSION : un `Set` module-level des ids déjà comptés → revenir
 *    sur la MÊME photo ne recompte pas (portée session SPA, remis à zéro au reload).
 *
 * `POST /api/assets/:id/view` est exempté du rate-limit côté serveur ; un compteur
 * raté n'est pas critique → échec silencieux.
 */

/** Délai (ms) avant de compter une vue : l'asset doit rester affiché ce temps-là. */
export const VIEW_DEBOUNCE_MS = 800;

/** Ids déjà comptés dans cette session SPA (dédoublonnage, portée module). */
const countedInSession = new Set<string>();

/** Réinitialise le dédoublonnage de vues (utile aux tests). */
export function resetViewTracker(): void {
  countedInSession.clear();
}

/**
 * Compte une vue pour `assetId` après `VIEW_DEBOUNCE_MS`, une seule fois par photo
 * et par session. `null` (aucun asset) est inerte.
 */
export function useViewTracker(assetId: string | null): void {
  useEffect(() => {
    if (assetId === null || countedInSession.has(assetId)) return;
    const timer = window.setTimeout(() => {
      // Marque AVANT l'appel : même si un re-render relance l'effet, on ne double pas.
      countedInSession.add(assetId);
      void incrementView(assetId).catch(() => {
        // Échec non critique : on ne re-tente pas (déjà marqué comme compté).
      });
    }, VIEW_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [assetId]);
}
